import { forwardRef } from 'react';

import { Platform } from '../../platform';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { Button } from '../Button';
import { TOGGLE_ICON_SIZE_FOR, TOGGLE_SELECTED_ICON_COLOR } from './ToggleButton.icons';
import type { ToggleButtonProps } from './ToggleButton.types';
import { toggleButtonVariants } from './ToggleButton.variants';

const IS_WEB = Platform.OS === 'web';

// Detached ref shape — see Pressable.tsx / Button.tsx for the rationale
// (pnpm-hoisted duplicate @types/react copies make React's `Ref<T>`
// nominally distinct across consumer apps).
type AnyRef<T> = ((node: T | null) => void) | { current: T | null } | null;

/**
 * Cross-platform icon-only toggle button — a Button that holds a
 * selected/unselected state (editor Bold/Italic, filter pins, favourite
 * stars). Mirrors the Figma "Toggle Icon button" set (`3832:17856`).
 *
 * Composition: wraps `<Button variant="tertiary">` (whose glass chip IS the
 * resting spec) and layers the selected/ghost deltas via `className` — see
 * `ToggleButton.variants.ts` for the palette and the no-shadow decision.
 *
 * Controlled-only — the caller owns the boolean:
 *
 * @example
 * ```tsx
 * import { ToggleButton } from '@flashka/shared/ui';
 * import { Bold } from '@flashka/shared/ui/icons';
 *
 * const [bold, setBold] = useState(false);
 * <ToggleButton icon={Bold} selected={bold} onPress={() => setBold(!bold)}
 *               accessibilityLabel="Bold" />
 *
 * // Ghost variant for dense toolbars
 * <ToggleButton variant="ghost" size="s" icon={Italic} selected={italic}
 *               onPress={() => setItalic(!italic)} accessibilityLabel="Italic" />
 * ```
 */
function ToggleButtonInner(
  {
    icon,
    label,
    selected = false,
    onPress,
    variant = 'outlined',
    size = 'm',
    disabled = false,
    accessibilityLabel,
    className,
    tooltipProps,
    testID,
    haptic,
    webProps,
    nativeProps,
    onHoverIn,
    onHoverOut,
    // Slot-style consumers (Radix Tooltip / Popover `Trigger asChild`) clone
    // the element and attach extra top-level props — onClick / onPointerDown /
    // aria-* / data-state — plus a ref for positioning. Funnel them through
    // Button, whose own `...injected` spread hands them to Pressable.
    ...injected
  }: ToggleButtonProps & Record<string, unknown>,
  ref: AnyRef<HTMLElement>,
) {
  const { isDark } = useTheme();

  // Announce the two-state semantics: `aria-pressed` (web) /
  // `accessibilityState.selected` (native). Caller props win on key overlap
  // (Toggle's aria-checked pattern).
  const mergedWebProps = { 'aria-pressed': selected, ...(webProps ?? {}) };
  const mergedNativeProps = {
    accessibilityState: { selected, disabled },
    ...(nativeProps ?? {}),
  };

  // Web icons inherit the container's text colour (`currentColor`), so the
  // CVA's `text-primary-800` handles the selected tint. Native SVG can't —
  // pass the resolved hex; unselected falls back to Button's tertiary grey.
  const iconColor =
    !IS_WEB && selected
      ? isDark
        ? TOGGLE_SELECTED_ICON_COLOR.dark
        : TOGGLE_SELECTED_ICON_COLOR.light
      : undefined;

  // Two content modes (mirrors Button): icon-only box vs. a text toggle with
  // an optional leading glyph. The selected palette is identical either way.
  const hasLabel = label !== undefined && label !== null && label !== '';

  return (
    <Button
      {...injected}
      ref={ref}
      variant="tertiary"
      size={size}
      icon={hasLabel ? undefined : icon}
      leftIcon={hasLabel ? icon : undefined}
      iconSize={hasLabel ? undefined : TOGGLE_ICON_SIZE_FOR[size]}
      iconColor={iconColor}
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      tooltipProps={tooltipProps}
      testID={testID}
      haptic={haptic}
      webProps={mergedWebProps}
      nativeProps={mergedNativeProps}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      className={cn(toggleButtonVariants({ variant, selected }), className)}
      // Icon-only mode tints the glyph via currentColor / iconColor, but the
      // label is Button's own <Text> (tertiary grey) — carry the selected tint
      // onto it explicitly so a selected text toggle reads primary-800.
      textClassName={selected ? 'text-primary-800' : undefined}>
      {hasLabel ? label : undefined}
    </Button>
  );
}

/**
 * Public ToggleButton. `forwardRef` so `asChild` consumers (Radix Tooltip /
 * Popover / Dropdown triggers) can reach the underlying DOM element for
 * positioning — same detached-ref cast as Button.
 */
export const ToggleButton = forwardRef(
  ToggleButtonInner as never,
) as unknown as React.ForwardRefExoticComponent<
  ToggleButtonProps & { ref?: AnyRef<HTMLElement> }
>;
(ToggleButton as { displayName?: string }).displayName = 'ToggleButton';
